import { apiV2, errorHandler } from "./index";
import { API_ERROR } from "../constants/ActionTypes";
import Swal from "sweetalert2";

function downloadFile(blob, fileName) {
  var newBlob = new Blob([blob], {
    type: "text/csv",
  });

  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(newBlob);
    return;
  }

  const data = window.URL.createObjectURL(newBlob);
  var link = document.createElement("a");
  link.href = data;
  link.download = fileName + ".csv";
  link.click();
  Swal.fire({
    title: "Report downloaded!",
    icon: "success",
    html: `
        <div style="text-align: center;">
          <p>If file did not automatically download please click the download link below.</p>
          <div style="padding: 10px">
            <a
              href="${data}"
              class="LargeButton"
              style="color: #000; padding: 10px 15px;"
              download="${fileName}.csv"
            >
              Download
            </a>
          </div>
        </div>
      `,
  }).then(() => {
    window.URL.revokeObjectURL(data);
  });
}

/**
 * Export feedbacks data to CSV
 *
 * Updated per Monday.com Task #10746389124:
 * - Now accepts optional search parameter to filter exported data
 * - Report now includes User ID and excludes Reference Number
 *
 * @param {Object} options - Export options
 * @param {string} [options.search] - Optional search term to filter feedbacks
 */
export function exportFeedbacksDataCSV(options = {}) {
  var d = new Date();
  var dateString = d.toLocaleString();

  return function (dispatch) {
    Swal.fire({
      title: "Loading...",
      animation: false,
      allowOutsideClick: true,
      allowEscapeKey: true,
      showConfirmButton: false,
    });

    const params = options.search ? `?search=${encodeURIComponent(options.search)}` : "";

    apiV2
      .get(`/feedbacks/report${params}`, { responseType: "blob" })
      .then(response => {
        let fileName = "Session-Feedbacks-Report-" + dateString;
        downloadFile(response.data, fileName);
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}

export function exportIntensityDataCSV({ from_date, to_date }) {
  return function (dispatch) {
    return apiV2
      .post(`/sessions/intensity-report`, {
        from_date,
        to_date,
      })
      .then(response => {
        const jobId = response.data?.data?.job_id || response.data?.job_id;
        if (!jobId) {
          throw new Error("Invalid response: job_id not found");
        }

        // Return job_id so component can immediately add it to the list and start polling
        // Component will handle showing success toast
        return Promise.resolve(jobId);
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        return Promise.reject(error);
      });
  };
}

export function fetchIntensityReports() {
  return function (dispatch) {
    return apiV2
      .get(`/sessions/intensity-report`)
      .then(response => {
        const reports = response.data?.data || response.data || [];
        return Promise.resolve(reports);
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        return Promise.resolve([]);
      });
  };
}

export function checkIntensityReportStatus(jobId) {
  return function (dispatch) {
    return apiV2
      .get(`/sessions/intensity-report/${jobId}`)
      .then(response => {
        const job = response.data?.data || response.data;
        return Promise.resolve(job);
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        return Promise.resolve(null);
      });
  };
}

export function downloadIntensityReport(jobId, fileName) {
  return function (dispatch) {
    Swal.fire({
      title: "Downloading...",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    apiV2
      .get(`/sessions/intensity-report/${jobId}/download`, {
        responseType: "blob",
      })
      .then(response => {
        Swal.close();
        downloadFile(response.data, fileName || `Intensity-Report-${jobId}`);
      })
      .catch(error => {
        Swal.close();
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}

export function extendIntensityReportExpiration(jobId, additionalDays = 30) {
  return function (dispatch) {
    return apiV2
      .post(`/sessions/intensity-report/${jobId}/extend-expiration`, {
        additional_days: additionalDays,
      })
      .then(response => {
        const job = response.data?.data || response.data;
        return Promise.resolve(job);
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
        return Promise.reject(error);
      });
  };
}

export function exportRequestsDataCSV() {
  var d = new Date();
  var dateString = d.toLocaleString();

  return function (dispatch) {
    Swal.fire({
      title: "Loading...",
      animation: false,
      allowOutsideClick: true,
      allowEscapeKey: true,
      showConfirmButton: false,
    });

    apiV2
      .get(`/requests/report`, { responseType: "blob" })
      .then(response => {
        let fileName = "Requested-Topics-Report-" + dateString;
        downloadFile(response.data, fileName);
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}

export function exportCancellationDataCSV() {
  var d = new Date();
  var dateString = d.toLocaleString();

  return function (dispatch) {
    Swal.fire({
      title: "Loading...",
      animation: false,
      allowOutsideClick: true,
      allowEscapeKey: true,
      showConfirmButton: false,
    });

    apiV2
      .get(`/user-cancellations/report`, { responseType: "blob" })
      .then(response => {
        let fileName = "Latest-User-Cancellation-Report-" + dateString;
        downloadFile(response.data, fileName);
      })
      .catch(error => {
        errorHandler(dispatch, error.response, API_ERROR);
      });
  };
}
